import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, Input, computed, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from "@angular/forms";
import { NcmPreset } from "@interfaces/cnpj-lookup";
import { DataPage } from "@interfaces/data-page";
import { Ncm } from "@interfaces/ncm";
import { Property, Schema } from "@interfaces/schema";
import { Store } from "@ngrx/store";
import { ModalService } from "@services/modal.service";
import { NcmService } from "@services/ncm.service";
import { selectNcms, selectPNCompany } from "@state/selectors/user.selectors";
import cfopList from "@static/cfop.json";
import icmsCstList1 from "@static/cstIcms.json";
import icmsCstList2 from "@static/cstIcmsSn.json";
import pisCofinsCstList from "@static/cstPisCofins.json";
import icmsSchema from "@static/icmsSchema.json";
import mdList from "@static/mdbc.json";
import mdstList from "@static/mdbcst.json";
import ncmList from "@static/ncm.json";
import originList from "@static/origemIcms.json";
import { PaginatorState } from "primeng/paginator";
import { Subscription } from "rxjs";
import format from "src/app/utils/formatter.util";
import setupPages from "src/app/utils/setup-pages.util";

/**
 * IBS/CBS: ou os dois códigos preenchidos, ou nenhum (grupo omitido).
 */
export function ibscbsBothOrNoneValidator(
  group: AbstractControl
): ValidationErrors | null {
  const cst = String(group.get("cst")?.value ?? "").trim();
  const classificacao = String(group.get("classificacao")?.value ?? "").trim();

  if (!cst && !classificacao) return null;
  if (cst && classificacao) return null;

  return { ibscbsIncomplete: true };
}

type NcmPayload = {
  tributos?: {
    ibscbs?: { cst?: string | null; classificacao?: string | null } | null;
    icms?: {
      baseCalculo?: { modalidadeDeterminacao?: string | number };
      substituicaoTributaria?: {
        baseCalculo?: { modalidadeDeterminacao?: string | number };
      };
    };
  };
};

@Component({
  selector: "app-ncm-selector-tab-panel",
  templateUrl: "./ncm-selector-tab-panel.component.html",
  styleUrls: ["./ncm-selector-tab-panel.component.scss"]
})
export class NcmSelectorTabPanelComponent {
  ncms = this.store.selectSignal(selectNcms);
  editing = signal(false);
  toEdit = signal<Ncm | null>(null);
  currentPage = signal(0);
  pages = signal<DataPage<Ncm>[]>([]);

  company = this.store.selectSignal(selectPNCompany);

  /**
   * NCM sugerido pelo CNAE na aba "Meu CNPJ": abre o formulário de cadastro
   * já com o NCM selecionado. O `nonce` muda a cada clique para o mesmo NCM
   * poder ser reaberto; tributos continuam por conta do usuário.
   */
  @Input() set presetNcm(preset: NcmPreset | null | undefined) {
    if (!preset?.ncm) return;

    // Estava editando outro NCM: zerar `toEdit` faz `startEditing$` emitir
    // null depois e resetar o formulário — o flag pede para pular esse reset.
    if (this.toEdit()) {
      this.skipNextResetOnClearEdit = true;
      this.toEdit.set(null);
    }

    this.editing.set(true);
    this.form().reset();
    this.form().patchValue({ ncm: preset.ncm });
  }

  private skipNextResetOnClearEdit = false;

  onLoadNcms$ = toObservable(this.ncms).subscribe((ncms) => {
    this.pages.set(setupPages(ncms, 6));
  });

  startEditing$ = toObservable(this.toEdit).subscribe((toEdit) => {
    if (toEdit) {
      // Seta todos os campos do formulÃ¡rio
      // O patchValue do CST vai triggar automaticamente a subscription
      // que chama selectIcmsCst com os valores corretos de toEdit()
      this.form().patchValue({
        ncm: toEdit.ncm,
        cest: toEdit.cest,
        tributos: {
          icms: {
            cst: toEdit.tributos.icms.cst,
            origem: toEdit.tributos.icms.origem
          },
          pis: toEdit.tributos.pis,
          cofins: toEdit.tributos.cofins,
          ibscbs: {
            cst: toEdit.tributos.ibscbs?.cst ?? "",
            classificacao: toEdit.tributos.ibscbs?.classificacao ?? ""
          }
        }
      });
    } else if (this.skipNextResetOnClearEdit) {
      this.skipNextResetOnClearEdit = false;
    } else {
      this.form().reset();
    }
  });

  ncmList = ncmList;
  cfopList = cfopList;
  mdList = mdList;
  mdstList = mdstList;
  icmsCstList1 = icmsCstList1;
  icmsCstList2 = icmsCstList2;
  pisCofinsCstList = pisCofinsCstList;
  icmsSchema: Schema = icmsSchema;
  originList = originList;

  form = computed(
    () =>
      new FormGroup({
        ncm: new FormControl("", Validators.required),
        cest: new FormControl(""),
        tributos: new FormGroup({
          icms: new FormGroup({
            cst: new FormControl("", Validators.required),
            origem: new FormControl("", Validators.required)
          }),
          pis: new FormGroup({
            cst: new FormControl("", Validators.required),
            aliquota: new FormControl<number>(0, Validators.required),
            baseCalculo: new FormGroup({
              valor: new FormControl<number>(0, Validators.required)
            })
          }),
          cofins: new FormGroup({
            cst: new FormControl("", Validators.required),
            aliquota: new FormControl<number>(0, Validators.required),
            baseCalculo: new FormGroup({
              valor: new FormControl<number>(0, Validators.required)
            })
          }),
          // Reforma Tributária (NT 2025.002): CST + cClassTrib do IBS/CBS.
          // Opcional (Simples/MEI só a partir de 2027), mas se um for
          // preenchido o outro passa a ser obrigatório.
          ibscbs: new FormGroup(
            {
              cst: new FormControl("", Validators.pattern(/^\d{3}$/)),
              classificacao: new FormControl("", Validators.pattern(/^\d{6}$/))
            },
            { validators: ibscbsBothOrNoneValidator }
          )
        })
      })
  );

  get icms() {
    return this.form().get("tributos")?.get("icms");
  }

  $: (Subscription | undefined)[] = [];

  constructor(
    private modalService: ModalService,
    private destroyRef: DestroyRef,
    private store: Store,
    private ncmService: NcmService
  ) {
    this.$.push(
      this.icms?.get("cst")?.valueChanges.subscribe((cst) => {
        this.selectIcmsCst(cst ?? "");
      })
    );

    // Aplica validação quando regime tributário mudar
    this.$.push(
      toObservable(this.company).subscribe(() => {
        this.applyBaseCalculoValidation();
      })
    );

    this.destroyRef.onDestroy(() => {
      this.$.forEach(($) => $?.unsubscribe());
    });
  }

  startEditing(ncm: Ncm) {
    this.editing.set(true);
    this.toEdit.set(ncm);
  }

  newNcm() {
    this.editing.set(true);
  }

  close() {
    this.editing.set(false);
    this.form().reset();
    this.toEdit.set(null);

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }

  discardChanges() {
    this.modalService.confirm("Dados não salvos serão perdidos", () => {
      const data = this.toEdit();
      this.form().reset(data ? data : {});
    });
  }

  selectIcmsCst(cst: string) {
    const schema = this.icmsSchema[cst];

    this.generateIcmsSchema(
      schema?.properties || [],
      this.icms as FormGroup,
      ["cst", "origem"],
      (this.toEdit()?.tributos.icms ?? {}) as never
    );

    // Para LP/LR, torna "Valor da BC" obrigatório se o campo baseCalculo existir
    this.applyBaseCalculoValidation();
  }

  /**
   * Aplica validação condicional no campo "Valor da BC" para LP/LR
   * Quando regime é LP (3) ou LR (4), o campo deve ser obrigatório
   */
  private applyBaseCalculoValidation() {
    const baseCalculoControl = this.icms?.get("baseCalculo");
    if (!baseCalculoControl || !(baseCalculoControl instanceof FormGroup))
      return;
    const baseCalculoGroup = baseCalculoControl;

    const regimeTributario = Number(this.company()?.regimeTributario || 0);
    const isLucroPresumidoOuReal =
      regimeTributario === 3 || regimeTributario === 4;

    const valorControl = baseCalculoGroup.get("valor");
    if (valorControl) {
      if (isLucroPresumidoOuReal) {
        // Para LP/LR, torna obrigatório
        valorControl.setValidators([Validators.required]);
      } else {
        // Para Simples Nacional, remove obrigatoriedade
        valorControl.clearValidators();
      }
      valorControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  generateIcmsSchema(
    props: Property[],
    fg: FormGroup,
    skipProps: string[],
    entityLike: never
  ) {
    Object.entries(fg.controls).forEach((entry) => {
      if (skipProps?.find((prop) => prop === entry[0])) return;

      fg.removeControl(entry[0], {
        emitEvent: true
      });
    });

    props?.forEach((prop) => {
      const isSchema = !!prop.properties?.length;

      if (isSchema) {
        fg.addControl(prop.name, new FormGroup({}));

        return this.generateIcmsSchema(
          prop.properties ?? [],
          fg.get(prop.name) as FormGroup,
          [],
          entityLike?.[prop.name]
        );
      }

      const rawValue = entityLike?.[prop.name] ?? prop?.defaultValue ?? "";
      const normalizedValue =
        typeof rawValue === "number" ? String(rawValue) : rawValue;

      const control = new FormControl(normalizedValue, {
        validators: prop.required ? Validators.required : []
      });

      fg.addControl(prop.name, control, {
        emitEvent: true
      });
    });
  }

  normalizePayload<T>(payload: T): T {
    const normalizable = payload as T & NcmPayload;

    if (normalizable.tributos?.icms?.baseCalculo?.modalidadeDeterminacao) {
      normalizable.tributos.icms.baseCalculo.modalidadeDeterminacao = Number(
        normalizable.tributos.icms.baseCalculo.modalidadeDeterminacao
      );
    }

    if (
      normalizable.tributos?.icms?.substituicaoTributaria?.baseCalculo
        ?.modalidadeDeterminacao
    ) {
      normalizable.tributos.icms.substituicaoTributaria.baseCalculo.modalidadeDeterminacao =
        Number(
          normalizable.tributos.icms.substituicaoTributaria.baseCalculo
            .modalidadeDeterminacao
        );
    }

    // Grupo vazio = não enviar IBS/CBS (o nf-server também trata, mas assim
    // o payload fica explícito).
    const ibscbs = normalizable.tributos?.ibscbs;
    if (
      ibscbs &&
      !String(ibscbs.cst ?? "").trim() &&
      !String(ibscbs.classificacao ?? "").trim()
    ) {
      delete normalizable.tributos!.ibscbs;
    }

    return payload;
  }

  submit() {
    const payload = this.normalizePayload(this.form().value);

    this.$.push(
      this.ncmService.save(payload).subscribe({
        next: () => {
          this.modalService.success(undefined, () => {
            this.close();
          });
        },
        error: (err: HttpErrorResponse) => {
          this.modalService.error(this.extractErrorMessage(err));
        }
      })
    );
  }

  deleteNcm(ncm: Ncm) {
    this.modalService.deletion(
      undefined,
      "Deletar um NCM pode gerar impactos em configurações de produtos que o utilizem.",
      () => {
        this.$.push(
          this.ncmService.delete(ncm.id).subscribe({
            next: () => {
              this.modalService.success();
            },
            error: (err: HttpErrorResponse) => {
              this.modalService.error(this.extractErrorMessage(err));
            }
          })
        );
      },
      undefined,
      {
        label: `Digite: ${format(ncm.ncm, "ncm")}`,
        required: true,
        fieldName: "confirmation",
        isValid: (value) => {
          return value === format(ncm.ncm, "ncm");
        }
      }
    );
  }

  onPageChange(state: PaginatorState) {
    this.currentPage.set((state.page ?? 0) + 1);
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    const candidates = [
      err?.error?.message,
      err?.error?.error?.message,
      err?.error?.message?.message,
      err?.message
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) {
        return candidate.join("\n");
      }

      if (typeof candidate === "string" && candidate.trim()) {
        return candidate;
      }
    }

    return "Ocorreu um erro ao processar a solicitação.";
  }
}
